import { useCallback, useState } from 'react'
import { useApolloClient } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import type {
  AttachmentVariantUrlQuery,
  AttachmentVariantUrlQueryVariables,
} from '@/graphql/queries/attachmentVariantUrlQuery.gql.generated'
import type {
  ChatFileDownloadQuery,
  ChatFileDownloadQueryVariables,
} from '@/graphql/queries/chatFileDownloadQuery.gql.generated'
import { AttachmentVariantUrlDocument } from '@/graphql/queries/attachmentVariantUrlQuery.gql.generated'
import { ChatFileDownloadDocument } from '@/graphql/queries/chatFileDownloadQuery.gql.generated'

function openDownload(url: string, fileName: string) {
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.rel = 'noreferrer'
  document.body.append(link)
  link.click()
  link.remove()
}

type Target =
  | { kind: 'chat-file'; id: string; fileName: string }
  | { kind: 'attachment'; id: string; fileName: string }

/**
 * Resolves a short-lived download URL at click time. URLs are never stored in
 * state or transcripts, so an old card keeps working after links expire.
 */
export function useChatFileDownload() {
  const client = useApolloClient()
  const { t } = useTranslation()
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const download = useCallback(
    async (target: Target) => {
      setBusy(target.id)
      setError(null)
      try {
        if (target.kind === 'chat-file') {
          const { data } = await client.query<
            ChatFileDownloadQuery,
            ChatFileDownloadQueryVariables
          >({
            query: ChatFileDownloadDocument,
            variables: { fileId: target.id },
            fetchPolicy: 'network-only',
          })
          if (!data) throw new Error('No download')
          openDownload(
            data.chatFileDownload.url,
            data.chatFileDownload.fileName,
          )
        } else {
          const { data } = await client.query<
            AttachmentVariantUrlQuery,
            AttachmentVariantUrlQueryVariables
          >({
            query: AttachmentVariantUrlDocument,
            variables: { attachmentId: target.id, variant: 'original' },
            fetchPolicy: 'network-only',
          })
          if (!data) throw new Error('No download')
          openDownload(data.attachmentVariantUrl, target.fileName)
        }
      } catch {
        setError(t('aiChat:files.downloadFailed', { name: target.fileName }))
      } finally {
        setBusy(null)
      }
    },
    [client, t],
  )

  return { download, busy, error }
}
